
//#region import libraries
import React, { useEffect, useMemo, useState } from "react";
// bootstrap
import { Row, Button, Form, Col } from 'react-bootstrap';
//tremor
import { Subtitle, Title } from '@tremor/react'

//custom components
import { CustomSpinner } from '_atoms';
import { ActionsModal } from '_components/molecules';
import QuestionForm from "./questions-form";
import SurveyPreviewView from "./survey-preview-view";
import { flattenSurveyForLanguage } from "./surveys-utils";
import {
    createEmptyQuestion, syncQuestionLanguages, validateSurvey, cleanQuestionsForSave, isQuestionBlank,
} from "./survey-validation";

//select-react
import Select from "react-select"
//redux
import { useDispatch, useSelector } from "react-redux";
import { selectIata } from "_redux/slices/main-screen/user";
import { postSurveyQuestionnaire, selectPostQuestionnaireStatus } from "_redux/slices/analytics/review-control-panel";
//styles
import { Colors } from "_styles";

import { FaEye, FaTriangleExclamation, FaCircleCheck } from 'react-icons/fa6';

//#endregion

//we can add more languages since it will be full dynamically
const LANGUAGES = [
    { label: 'English', value: "en" },
    { label: 'Arabic', value: "ar" },
    { label: 'Spanish', value: "es" },
];


const BORDER = '#e5e7eb';
const WARN = '#C0392B';
const MUTED = '#6b7280';
const labelStyle = { color: Colors.PRM_LIGHT_BLUE, fontWeight: 500, marginBottom: 2 };
const sectionStyle = { border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, background: '#fff' };

/**
 * Single-screen survey builder: settings and questions on the left, live passenger
 * preview on the right. Saved surveys are created inactive (draft).
 *
 * @param handleCancel  () => void — close the builder
 * @param initialDraft  optional { groupTitle, surveyType, languages, questions } (Duplicate)
 */
const SurveyQuestionnaireForm = ({ handleCancel, initialDraft }) => {

    //#region redux
    const dispatch = useDispatch();
    const postQuestionnaireStatus = useSelector(selectPostQuestionnaireStatus);
    const iata = useSelector(selectIata);
    //#endregion

    //#region local state
    const [surveyType] = useState(initialDraft?.surveyType || "arrivals");
    const [groupTitle, setGroupTitle] = useState(initialDraft?.groupTitle || "");
    const [selectedLanguages, setSelectedLanguages] = useState(initialDraft?.languages || [LANGUAGES[0]]);
    const [questions, setQuestions] = useState(() =>
        initialDraft?.questions?.length ? initialDraft.questions : [createEmptyQuestion(initialDraft?.languages || [LANGUAGES[0]])]
    );
    const [expandedKeys, setExpandedKeys] = useState(() => new Set(initialDraft?.questions?.length ? [] : [questions[0]._key]));
    const [showErrors, setShowErrors] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewLang, setPreviewLang] = useState(selectedLanguages[0]?.value || "en");
    //#endregion

    const { surveyErrors, questionErrors, isValid } = useMemo(
        () => validateSurvey({ groupTitle, languages: selectedLanguages, questions }),
        [groupTitle, selectedLanguages, questions]
    );
    const invalidCount = Object.keys(questionErrors).length;
    const isDirty = !!groupTitle.trim() || questions.some((q) => !isQuestionBlank(q));
    const saving = postQuestionnaireStatus === "loading";

    // The live preview re-runs the full passenger renderer, so let typing settle before refreshing it.
    const [previewSource, setPreviewSource] = useState(questions);
    useEffect(() => {
        const t = setTimeout(() => setPreviewSource(questions), 300);
        return () => clearTimeout(t);
    }, [questions]);

    // Preview only questions with a type chosen.
    const previewQuestions = useMemo(
        () => flattenSurveyForLanguage({ questions: previewSource.filter((q) => q?.type) }, previewLang),
        [previewSource, previewLang]
    );

    //#region functions
    const handleLanguagesChange = (selection) => {
        const next = selection || [];
        if (next.length === 0) return; // at least one language is always required

        const removed = selectedLanguages.filter((l) => !next.some((n) => n.value === l.value));
        const removedWithText = removed.filter((l) => questions.some((q) => (q.text?.[l.value] || '').trim()));
        if (removedWithText.length && !window.confirm(`Remove ${removedWithText.map((l) => l.label).join(', ')}? Text entered in that language will be lost.`)) return;

        setSelectedLanguages(next);
        setQuestions(questions.map((q) => syncQuestionLanguages(q, next)));
        if (!next.some((l) => l.value === previewLang)) setPreviewLang(next[0].value);
    };

    const saveSurvey = () => {
        if (!isValid) {
            setShowErrors(true);
            // Open every problem question and jump to the first problem.
            setExpandedKeys(new Set([...expandedKeys, ...Object.keys(questionErrors)]));
            const firstKey = questions.find((q) => questionErrors[q._key])?._key;
            const target = surveyErrors.length && (!groupTitle.trim() || !selectedLanguages.length)
                ? document.getElementById('survey-settings')
                : firstKey ? document.getElementById(`question-card-${firstKey}`) : null;
            setTimeout(() => target?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
            return;
        }
        dispatch(postSurveyQuestionnaire({
            iata,
            surveyType,
            groupTitle: groupTitle.trim(),
            active: false,
            questions: cleanQuestionsForSave(questions),
            languages: selectedLanguages,
        }));
    };

    const onCancel = () => {
        if (isDirty && !window.confirm("Discard this survey? Your changes will be lost.")) return;
        handleCancel?.();
    };
    //#endregion

    const statusText = (() => {
        if (postQuestionnaireStatus === "failed") return { tone: WARN, icon: true, text: "Couldn't save the survey — please try again." };
        if (!showErrors) return { tone: MUTED, text: "Saved as a draft — activate it from the survey list when you're ready." };
        if (isValid) return { tone: '#2E7D32', ok: true, text: "Ready to save." };
        const parts = [...surveyErrors];
        if (invalidCount) parts.push(`${invalidCount} question${invalidCount > 1 ? 's' : ''} need${invalidCount > 1 ? '' : 's'} attention`);
        return { tone: WARN, icon: true, text: parts.join(' · ') };
    })();

    const previewPanel = (
        <SurveyPreviewView
            questions={previewQuestions}
            languages={selectedLanguages}
            language={previewLang}
            onLanguageChange={setPreviewLang}
        />
    );

    return (
        <div className="mt-3" style={{ paddingBottom: 96 }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div>
                    <Title>{initialDraft ? "Duplicate survey" : "Create survey"}</Title>
                    <Subtitle>Name the survey and choose its languages, then add your questions. The preview updates as you type.</Subtitle>
                </div>
                <Button className="d-lg-none" variant="outline-primary" size="sm" onClick={() => setShowPreviewModal(true)}>
                    <FaEye style={{ marginRight: 6 }} /> Preview
                </Button>
            </div>

            <Row className="mt-3">
                {/* Builder */}
                <Col lg={7}>
                    {/* Settings */}
                    <div id="survey-settings" style={sectionStyle}>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Survey name</Form.Label>
                                    <Form.Text as="div" muted style={{ marginTop: 0, marginBottom: 6 }}>Only you see this — passengers don't.</Form.Text>
                                    <Form.Control
                                        value={groupTitle}
                                        onChange={(e) => setGroupTitle(e.target.value)}
                                        placeholder="e.g. Arrivals assistance — Q4"
                                        isInvalid={showErrors && !groupTitle.trim()}
                                    />
                                    <Form.Control.Feedback type="invalid">Give the survey a name</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mt-3 mt-md-0">
                                <Form.Group>
                                    <Form.Label style={labelStyle}>Languages</Form.Label>
                                    <Form.Text as="div" muted style={{ marginTop: 0, marginBottom: 6 }}>Each question needs text in every language.</Form.Text>
                                    <Select
                                        isMulti
                                        isClearable={false}
                                        options={LANGUAGES}
                                        classNamePrefix="select"
                                        onChange={handleLanguagesChange}
                                        value={selectedLanguages}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                    </div>

                    {/* Questions */}
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '24px 0 10px' }}>
                        <Form.Label style={{ ...labelStyle, fontSize: 18, margin: 0 }}>Questions ({questions.length})</Form.Label>
                        <span style={{ fontSize: 12, color: MUTED }}>Drag ⋮⋮ to reorder</span>
                    </div>

                    <QuestionForm
                        questions={questions}
                        setQuestions={setQuestions}
                        languages={selectedLanguages}
                        questionErrors={questionErrors}
                        showErrors={showErrors}
                        expandedKeys={expandedKeys}
                        setExpandedKeys={setExpandedKeys}
                    />
                </Col>

                {/* Live preview */}
                <Col lg={5} className="d-none d-lg-block">
                    <div style={{ position: 'sticky', top: 16 }}>
                        {previewPanel}
                    </div>
                </Col>
            </Row>

            {/* Sticky action bar */}
            <div
                style={{
                    position: 'sticky', bottom: 0, zIndex: 5, marginTop: 24,
                    display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px',
                    background: '#fff', borderTop: `1px solid ${BORDER}`, boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
                }}
            >
                <Button variant="outline-secondary" style={{ width: 140 }} onClick={onCancel} disabled={saving}>Cancel</Button>
                <span style={{ flex: 1, fontSize: 14, color: statusText.tone, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {statusText.icon && <FaTriangleExclamation />}
                    {statusText.ok && <FaCircleCheck />}
                    {statusText.text}
                </span>
                <Button variant="primary" style={{ width: 180 }} onClick={saveSurvey} disabled={saving}>
                    {saving ? <CustomSpinner /> : "Save as draft"}
                </Button>
            </div>

            {/* Preview fallback for narrow screens */}
            <ActionsModal
                show={showPreviewModal}
                handleClose={() => setShowPreviewModal(false)}
                title={groupTitle ? `Preview: ${groupTitle}` : "Survey Preview"}
                subtitle={"This is how passengers will see the survey. Responses are not saved."}
                minWidth="620px"
                maxWidth="620px"
            >
                {previewPanel}
            </ActionsModal>
        </div>
    )
}

export default SurveyQuestionnaireForm;
